import { Flex, Spinner, Table } from '@radix-ui/themes';
import useOnScreen from 'hooks/useOnScreen';
import throttle from 'lodash/throttle';
import { useEffect, useRef } from 'react';

const THROTTLE_DELAY_MS = 300;

interface IProps {
  hasMore?: boolean;
  rootMargin?: string;
  colSpan: number;
  onLoadMore?: () => void;
}

export const TableInfiniteLoader = ({
  hasMore,
  colSpan,
  rootMargin,
  onLoadMore,
}: IProps) => {
  // so we have a stable reference to the throttled function between renders
  const throttledLoadMoreRef = useRef<() => void>();
  const initializedRef = useRef(false);

  const { isVisible, ref: loaderRef } = useOnScreen({
    rootMargin,
    threshold: 0,
    disabled: !hasMore,
  });

  useEffect(() => {
    if (!onLoadMore) {
      return;
    }

    const delay = initializedRef.current ? THROTTLE_DELAY_MS : 0;
    const throttled = throttle(onLoadMore, delay, {
      leading: false,
      trailing: true,
    });

    throttledLoadMoreRef.current = throttled;

    return () => {
      throttled.cancel();
      initializedRef.current = true;
    };
  }, [onLoadMore]);

  useEffect(() => {
    if (throttledLoadMoreRef.current && isVisible && hasMore) {
      throttledLoadMoreRef.current();
    }
  }, [isVisible, hasMore]);

  return (
    <Table.Row className="hover-disabled">
      <Table.Cell colSpan={colSpan}>
        <Flex ref={loaderRef} p="5" align="center" justify="center">
          <Spinner size="3" />
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};
